import { useState, useEffect } from 'react'
import { Play, Square, Radio, AlertCircle, Clock, Wifi, WifiOff } from 'lucide-react'
import { useTradingStore } from '../hooks/useTradingStore'

const COMMON_LIVE_INSTRUMENTS = [
  'M2K.CME',
  'ES.CME',
  'NQ.CME',
  'MES.CME',
  'MNQ.CME',
  'YM.CME',
  'RTY.CME',
  'GC.CME',
  'SI.CME',
  'CL.CME',
  '1OZ.COMEX',
  'M6E.CME',
]

export default function LiveTrading() {
  const {
    availableStrategies,
    activeStrategy,
    selectedStrategyId,
    selectedPreset,
    strategyStatus,
    runMode,
    metrics,
    setRunMode,
    selectStrategy,
    startSelectedStrategy,
    stopSelectedStrategy,
    paramOverrides,
  } = useTradingStore()

  const [instrument, setInstrument] = useState('')
  const [customInstrument, setCustomInstrument] = useState('')
  const [useCustom, setUseCustom] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const isRunning = strategyStatus === 'running' && runMode === 'live'
  const currentStrategy = availableStrategies.find(s => s.id === selectedStrategyId)

  // Set default instrument from strategy
  useEffect(() => {
    if (currentStrategy?.default_instrument_live) {
      setInstrument(currentStrategy.default_instrument_live)
    }
  }, [currentStrategy])

  // Elapsed time counter
  useEffect(() => {
    if (!isRunning) {
      setStartTime(null)
      return
    }
    if (!startTime) setStartTime(new Date())
    const interval = setInterval(() => {
      if (startTime) {
        setElapsed(Math.floor((Date.now() - startTime.getTime()) / 1000))
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [isRunning, startTime])

  const formatElapsed = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  const resolvedInstrument = useCustom ? customInstrument : instrument

  const handleStart = async () => {
    if (!resolvedInstrument || !resolvedInstrument.includes('.')) {
      setError("Invalid instrument format. Expected 'SYMBOL.VENUE' (e.g. 'M2K.CME')")
      return
    }
    setError(null)
    try {
      setRunMode('live')
      await startSelectedStrategy(resolvedInstrument)
      setStartTime(new Date())
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to start live trading')
    }
  }

  const handleStop = async () => {
    try {
      await stopSelectedStrategy()
      setElapsed(0)
    } catch (err: any) {
      setError(err.message || 'Failed to stop')
    }
  }

  // Group params by category (same logic as Backtest page)
  const groupParams = (params: Record<string, any>) => {
    const groups: Record<string, string[]> = {}
    for (const key of Object.keys(params)) {
      let group = 'Shared'
      if (key.endsWith('_a')) group = 'Side A'
      else if (key.endsWith('_b')) group = 'Side B'
      else if (key.startsWith('hmm_')) group = 'HMM'
      else if (key.startsWith('bocpd_')) group = 'BOCPD'
      else if (key.startsWith('vol_spike') || key.startsWith('vol_weighted')) group = 'Volume'
      else if (key.startsWith('circuit_breaker')) group = 'Circuit Breaker'
      else if (key.startsWith('kalman_')) group = 'Kalman'
      if (!groups[group]) groups[group] = []
      groups[group].push(key)
    }
    return groups
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radio size={24} className="text-green-400" />
          <h1 className="text-xl font-bold text-white">Live Trading</h1>
          {isRunning && (
            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30 animate-pulse">
              LIVE
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left column: Configuration */}
        <div className="col-span-2 space-y-4">
          {/* Strategy & Preset */}
          <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
            <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Strategy Configuration</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#666] mb-1 block">Strategy</label>
                <select
                  value={selectedStrategyId || ''}
                  onChange={(e) => {
                    const strat = availableStrategies.find(s => s.id === e.target.value)
                    selectStrategy(e.target.value, strat?.default_preset || strat?.presets[0] || '')
                  }}
                  disabled={isRunning}
                  className="input w-full"
                >
                  <option value="">Select strategy...</option>
                  {availableStrategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#666] mb-1 block">Preset</label>
                <select
                  value={selectedPreset || ''}
                  onChange={(e) => selectStrategy(selectedStrategyId!, e.target.value)}
                  disabled={isRunning || !currentStrategy}
                  className="input w-full"
                >
                  {currentStrategy?.presets.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Instrument Selection */}
          <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
            <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Instrument</h2>
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={() => setUseCustom(false)}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  !useCustom
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'text-[#888] hover:text-white border border-[#333]'
                }`}
              >
                Common Instruments
              </button>
              <button
                onClick={() => setUseCustom(true)}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  useCustom
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'text-[#888] hover:text-white border border-[#333]'
                }`}
              >
                Custom
              </button>
            </div>

            {useCustom ? (
              <input
                type="text"
                value={customInstrument}
                onChange={(e) => setCustomInstrument(e.target.value.toUpperCase())}
                placeholder="e.g. M2K.CME"
                disabled={isRunning}
                className="input w-full"
              />
            ) : (
              <select
                value={instrument}
                onChange={(e) => setInstrument(e.target.value)}
                disabled={isRunning}
                className="input w-full"
              >
                {COMMON_LIVE_INSTRUMENTS.map(inst => (
                  <option key={inst} value={inst}>{inst}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-[#555] mt-2">
              Default for this strategy: <span className="text-green-400">{currentStrategy?.default_instrument_live || 'N/A'}</span>
            </p>
          </div>

          {/* Strategy Parameters (read-only) */}
          {activeStrategy?.preset_params && Object.keys(activeStrategy.preset_params).length > 0 && (
            <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
              <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Strategy Parameters</h2>
              <div className="flex gap-6 overflow-x-auto">
                {Object.entries(groupParams(activeStrategy.preset_params)).map(([group, keys]) => (
                  <div key={group} className="flex-shrink-0 min-w-[200px]">
                    <div className="text-[10px] text-[#555] uppercase font-semibold mb-1.5 border-b border-[#2a2a2a] pb-1">{group}</div>
                    <div className="flex flex-col gap-1">
                      {keys.map((key) => {
                        const value = activeStrategy.preset_params![key]
                        return (
                          <div key={key} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-[#888] truncate">{key.replace(/_a$|_b$/, '')}</span>
                            <span className={`text-xs font-mono flex-shrink-0 ${
                              typeof value === 'boolean' ? (value ? 'text-green-400' : 'text-red-400') : 'text-white'
                            }`}>
                              {typeof value === 'boolean' ? (value ? 'True' : 'False') : typeof value === 'number' ? value : String(value)}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          {/* Parameter overrides: the Backtest tab's edits apply to live runs too. */}
          {(() => {
            const applied = isRunning ? activeStrategy?.param_overrides : paramOverrides
            if (!applied || Object.keys(applied).length === 0) return null
            const list = Object.entries(applied).map(([k, v]) => `${k}=${String(v)}`).join(', ')
            return (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm text-yellow-300">
                {isRunning
                  ? `Running with parameter overrides: ${list}`
                  : `Will start with the Backtest tab's parameter edits (${activeStrategy?.preset_name}): ${list} — Reset to Preset there to clear`}
              </div>
            )
          })()}

          {/* Start / Stop */}
          <div className="flex gap-3">
            {isRunning ? (
              <button
                onClick={handleStop}
                className="btn btn-danger flex items-center gap-2 px-6 py-3 text-base"
              >
                <Square size={18} />
                Stop Live Trading
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={!selectedStrategyId || !selectedPreset || !resolvedInstrument}
                className="btn btn-success flex items-center gap-2 px-6 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play size={18} />
                Start Live Trading
              </button>
            )}
          </div>
        </div>

        {/* Right column: Status */}
        <div className="space-y-4">
          {/* Connection Status */}
          <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
            <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888]">Mode</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  isRunning ? 'bg-green-500/20 text-green-400' : 'bg-[#1a1a1a] text-[#666]'
                }`}>
                  {isRunning ? 'LIVE' : 'IDLE'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888]">Connection</span>
                {isRunning ? (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <Wifi size={12} /> Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-[#666]">
                    <WifiOff size={12} /> Disconnected
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888]">Instrument</span>
                <span className="text-xs text-white font-mono">{resolvedInstrument || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#888]">Elapsed</span>
                <span className="text-xs text-white font-mono flex items-center gap-1">
                  <Clock size={10} />
                  {isRunning ? formatElapsed(elapsed) : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Live Metrics */}
          {isRunning && (
            <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
              <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Metrics</h2>
              <div className="space-y-2">
                {[
                  { label: 'P&L', display: `$${metrics.pnl.toFixed(2)}`, color: metrics.pnl >= 0 ? 'text-green-400' : 'text-red-400' },
                  { label: 'Realized', display: `$${metrics.realized_pnl.toFixed(2)}`, color: metrics.realized_pnl >= 0 ? 'text-green-400' : 'text-red-400' },
                  { label: 'Win Rate', display: `${metrics.win_rate.toFixed(1)}%`, color: 'text-white' },
                  { label: 'Trades', display: `${metrics.total_trades}`, color: 'text-white' },
                  { label: 'Max DD', display: metrics.max_drawdown == null ? '—' : `${metrics.max_drawdown.toFixed(2)}%`, color: 'text-red-400' },
                ].map(({ label, display, color }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-[#888]">{label}</span>
                    <span className={`text-xs font-mono ${color}`}>{display}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategy Info */}
          {currentStrategy && (
            <div className="p-4 bg-[#111] rounded-lg border border-[#2a2a2a]">
              <h2 className="text-sm font-medium text-[#888] uppercase mb-3">Strategy</h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#888]">Name</span>
                  <span className="text-xs text-white">{currentStrategy.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#888]">Preset</span>
                  <span className="text-xs text-blue-400">{selectedPreset}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#888]">Default Live</span>
                  <span className="text-xs text-green-400 font-mono">{currentStrategy.default_instrument_live || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
